import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/utils/cn'

export interface BulkActionBarProps {
  /** Number of selected rows/cards. Non-positive → bar unmounts. */
  count: number
  /** Called when the user hits Esc or clicks "Clear selection". */
  onClear: () => void
  /** Right-aligned action buttons (usually <Button variant="secondary" size="sm">). */
  children: React.ReactNode
  /** Optional label override — default: "{count} selected". */
  label?: React.ReactNode
  className?: string
}

/**
 * Non-modal selection bar that floats over the content area when rows are
 * selected (spec §Bulk action bar). Slides in / out; never reserves
 * vertical space. Esc clears selection.
 */
export function BulkActionBar({
  count,
  onClear,
  children,
  label,
  className,
}: BulkActionBarProps) {
  useEffect(() => {
    if (count <= 0) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClear()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [count, onClear])

  if (count <= 0 || typeof document === 'undefined') return null

  return createPortal(
    <div
      role="region"
      aria-label={`${count} selected — bulk actions`}
      className={cn(
        'pointer-events-none fixed inset-x-0 bottom-4 z-drawer flex justify-center px-4',
        className,
      )}
    >
      <div
        className={cn(
          'pointer-events-auto flex h-14 items-center gap-3 rounded-md border border-border-default bg-bg-inverse px-4 text-text-inverse shadow-lg',
          'animate-in slide-in-from-bottom-2 fade-in-0 duration-normal',
        )}
      >
        <span className="text-sm font-semibold tabular-nums">
          {label ?? `${count} selected`}
        </span>
        <button
          type="button"
          onClick={onClear}
          className="cursor-pointer border-none bg-transparent text-xs font-medium text-text-inverse opacity-70 hover:opacity-100"
        >
          Clear selection
          <span
            className="text-role-kbd-shortcut ml-1.5 inline-flex items-center rounded-xs px-1 opacity-80"
            style={{ background: 'var(--color-kbd-bg)' }}
          >
            Esc
          </span>
        </button>
        <span aria-hidden="true" className="mx-1 inline-block h-4 w-px bg-text-inverse opacity-30" />
        <div className="flex items-center gap-2">{children}</div>
      </div>
    </div>,
    document.body,
  )
}
